import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// Hardcoded course data
const courseData = {
  id: 2,
  title: 'Email Security Fundamentals',
  description: 'Master the essentials of email security and protection against common threats. This course covers the basics of identifying suspicious emails, understanding email headers, and best practices for email security.',
  modules: [
    {
      id: 1,
      title: 'Introduction to Email Security',
      type: 'lesson',
      completed: true,
      content: `
        <h2>Understanding Email Security Fundamentals</h2>
        <p>Email remains one of the primary vectors for cyberattacks. This module introduces the core concepts of email security and why it matters to organizations of all sizes.</p>
        
        <h3>Key Topics Covered:</h3>
        <ul>
          <li>Email's role in modern business</li>
          <li>Common email-based threats</li>
          <li>The cost of email security breaches</li>
          <li>Basic principles of email security</li>
        </ul>
        
        <p>By the end of this module, you'll understand why email security is critical and how it fits into your organization's broader security posture.</p>
      `
    },
    {
      id: 2,
      title: 'Recognizing Phishing Attempts',
      type: 'lesson',
      completed: true,
      content: `
        <h2>How to Identify Phishing Emails</h2>
        <p>Phishing emails are designed to trick you into revealing sensitive information or installing malware. This module teaches you how to spot the red flags.</p>
        
        <h3>Common Phishing Indicators:</h3>
        <ul>
          <li>Urgent or threatening language</li>
          <li>Requests for sensitive information</li>
          <li>Suspicious sender addresses</li>
          <li>Grammatical errors and unusual formatting</li>
          <li>Suspicious links and attachments</li>
        </ul>
        
        <div class="bg-yellow-50 p-4 rounded-md my-4">
          <h4 class="text-yellow-800 font-medium">Remember:</h4>
          <p class="text-yellow-700">If an email creates a sense of urgency or seems too good to be true, it probably is. Always verify unexpected requests through a separate communication channel.</p>
        </div>
      `
    },
    {
      id: 3,
      title: 'Email Headers Analysis',
      type: 'lesson',
      completed: false,
      content: `
        <h2>Understanding Email Headers</h2>
        <p>Email headers contain valuable information that can help you determine if an email is legitimate. This module teaches you how to access and interpret email headers.</p>
        
        <h3>Key Header Fields:</h3>
        <ul>
          <li><strong>From:</strong> The sender's email address</li>
          <li><strong>Return-Path:</strong> Where replies will go (should match the From address)</li>
          <li><strong>Received:</strong> The path the email took to reach you</li>
          <li><strong>DKIM-Signature:</strong> Shows if the email has been verified by the sending domain</li>
          <li><strong>SPF:</strong> Indicates if the sender is authorized to send from that domain</li>
        </ul>
        
        <div class="bg-blue-50 p-4 rounded-md my-4">
          <h4 class="text-blue-800 font-medium">How to View Headers:</h4>
          <p class="text-blue-700">In most email clients, you can view headers by looking for options like "View Original" or "Show Headers" in the message options menu.</p>
        </div>
      `
    },
    {
      id: 4,
      title: 'Email Security Quiz',
      type: 'quiz',
      completed: false,
      questions: [
        {
          id: 1,
          question: 'Which of the following is NOT a common indicator of a phishing email?',
          options: [
            'Urgent or threatening language',
            'Professional formatting and correct grammar',
            'Requests for sensitive information',
            'Suspicious sender address'
          ],
          correctAnswer: 1
        },
        {
          id: 2,
          question: 'What should you do if you receive an unexpected email with an attachment?',
          options: [
            'Open it immediately to check its contents',
            'Forward it to your colleagues to get their opinion',
            'Verify the sender through a different communication channel before opening',
            'Reply to the email asking if it\'s legitimate'
          ],
          correctAnswer: 2
        },
        {
          id: 3,
          question: 'Which email header field shows the path an email took to reach you?',
          options: [
            'From',
            'Subject',
            'Received',
            'Content-Type'
          ],
          correctAnswer: 2
        },
        {
          id: 4,
          question: 'What does SPF stand for in email security?',
          options: [
            'Sender Policy Framework',
            'Secure Protection Filter',
            'Standard Protocol Format',
            'Spam Prevention Function'
          ],
          correctAnswer: 0
        },
        {
          id: 5,
          question: 'What is the best action to take if you suspect you\'ve received a phishing email at work?',
          options: [
            'Delete it immediately',
            'Report it to your IT security team',
            'Reply to the email asking if it\'s legitimate',
            'Click any links to see if they\'re suspicious'
          ],
          correctAnswer: 1
        }
      ]
    },
    {
      id: 5,
      title: 'Email Security Best Practices',
      type: 'lesson',
      completed: false,
      content: `
        <h2>Implementing Email Security Best Practices</h2>
        <p>This module covers the essential practices and habits that will help you maintain email security in your daily work.</p>
        
        <h3>Key Best Practices:</h3>
        <ul>
          <li>Use strong, unique passwords for email accounts</li>
          <li>Enable two-factor authentication whenever possible</li>
          <li>Be cautious with attachments and links</li>
          <li>Verify sender identities before responding to requests</li>
          <li>Keep your email client and security software updated</li>
          <li>Use encryption for sensitive communications</li>
          <li>Report suspicious emails to your IT security team</li>
        </ul>
        
        <div class="bg-green-50 p-4 rounded-md my-4">
          <h4 class="text-green-800 font-medium">Pro Tip:</h4>
          <p class="text-green-700">Create a separate email address for less important accounts to keep your primary inbox clean and reduce your exposure to potential threats.</p>
        </div>
      `
    }
  ],
  progress: 40,
  category: 'Email Security',
  duration: '1 hour',
  completionRate: 72,
};

const CourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [course, setCourse] = useState(null);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [currentModule, setCurrentModule] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [leaveWarning, setLeaveWarning] = useState(false);
  
  useEffect(() => {
    // Fetch course data
    const fetchCourse = async () => {
      try {
        // In a real app, this would be an API call
        // For now, we'll use our hardcoded data
        setIsLoading(true);
        setCourse(courseData);
        
        // Find the first incomplete module or use the first one
        const startIndex = courseData.modules.findIndex(module => !module.completed);
        setCurrentModuleIndex(startIndex !== -1 ? startIndex : 0);
        setCurrentModule(courseData.modules[startIndex !== -1 ? startIndex : 0]);
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching course:', error);
        setIsLoading(false);
      }
    };
    
    fetchCourse();
  }, [id]);
  
  const navigateToModule = (index) => {
    if (index >= 0 && index < course.modules.length) {
      // Check if we're leaving a quiz without submitting
      if (currentModule.type === 'quiz' && 
          !quizSubmitted && 
          Object.keys(quizAnswers).length > 0) {
        setLeaveWarning(true);
        return;
      }
      
      setCurrentModuleIndex(index);
      setCurrentModule(course.modules[index]);
      setQuizAnswers({});
      setQuizSubmitted(false);
      setQuizScore(0);
      setLeaveWarning(false);
    }
  };
  
  const navigateNext = () => {
    navigateToModule(currentModuleIndex + 1);
  };
  
  const navigatePrevious = () => {
    navigateToModule(currentModuleIndex - 1);
  };
  
  const handleQuizAnswerChange = (questionId, answerIndex) => {
    if (quizSubmitted) return;
    
    setQuizAnswers({
      ...quizAnswers,
      [questionId]: answerIndex
    });
  };
  
  const handleQuizSubmit = () => {
    if (quizSubmitted) {
      // Reset quiz to try again
      setQuizAnswers({});
      setQuizSubmitted(false);
      setQuizScore(0);
      return;
    }
    
    // Calculate score
    let correctAnswers = 0;
    currentModule.questions.forEach(question => {
      if (quizAnswers[question.id] === question.correctAnswer) {
        correctAnswers++;
      }
    });
    
    const score = Math.round((correctAnswers / currentModule.questions.length) * 100);
    setQuizScore(score);
    setQuizSubmitted(true);
    
    // In a real app, we would save the quiz results to an API
  };
  
  const handleMarkComplete = () => {
    // Update module completion status
    const updatedModules = [...course.modules];
    updatedModules[currentModuleIndex].completed = true;
    
    setCourse({
      ...course,
      modules: updatedModules
    });
    
    // Move to next module if available
    if (currentModuleIndex < course.modules.length - 1) {
      navigateNext();
    }
    
    // In a real app, we would save the completion status to an API
  };
  
  const confirmLeaveQuiz = () => {
    setLeaveWarning(false);
    setQuizAnswers({});
    navigateToModule(currentModuleIndex + 1);
  };
  
  const cancelLeaveQuiz = () => {
    setLeaveWarning(false);
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  return (
    <div className="bg-white shadow-sm rounded-lg">
      <div className="p-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{course.title}</h1>
            <p className="mt-1 text-gray-600">{course.description}</p>
            
            <div className="mt-4 flex items-center text-sm text-gray-500">
              <span className="mr-4">{course.category}</span>
              <span className="mr-4">{course.duration}</span>
              <span>Completion Rate: {course.completionRate}%</span>
            </div>
            
            <div className="mt-4">
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-700 mr-2">Your Progress: {course.progress}%</span>
                <span className="text-xs text-gray-500">{course.modules.filter(m => m.completed).length}/{course.modules.length} modules</span>
              </div>
              <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-primary-600 h-2 rounded-full" 
                  style={{ width: `${course.progress}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Module content area */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Module navigation sidebar */}
          <div className="md:col-span-1">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Modules</h2>
            <nav className="space-y-2">
              {course.modules.map((module, index) => (
                <button
                  key={module.id}
                  onClick={() => navigateToModule(index)}
                  className={`w-full text-left p-3 rounded-md flex items-center ${
                    currentModuleIndex === index 
                      ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-700' 
                      : 'hover:bg-gray-50'
                  }`}
                >
                  {module.completed && (
                    <svg className="h-5 w-5 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                  
                  {!module.completed && (
                    <div className={`h-5 w-5 mr-2 rounded-full border ${
                      currentModuleIndex === index ? 'border-primary-700' : 'border-gray-300'
                    }`}></div>
                  )}
                  
                  <div>
                    <div className={`font-medium ${currentModuleIndex === index ? 'text-primary-700' : 'text-gray-700'}`}>
                      {module.title}
                    </div>
                    <div className="text-xs text-gray-500">
                      {module.type === 'lesson' ? 'Lesson' : 'Quiz'}
                    </div>
                  </div>
                </button>
              ))}
            </nav>
          </div>
          
          {/* Content display area */}
          <div className="md:col-span-3">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {currentModule.title}
              </h2>
              
              {/* Leave quiz warning dialog */}
              {leaveWarning && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg max-w-md w-full p-6">
                    <h3 className="text-lg font-medium text-red-600 mb-4">Leave without submitting?</h3>
                    <p className="text-gray-700 mb-4">
                      You have started this quiz but haven't submitted your answers. If you leave now, your progress will be lost.
                    </p>
                    <div className="flex justify-end space-x-3">
                      <button 
                        onClick={cancelLeaveQuiz}
                        className="btn btn-outline"
                      >
                        Stay on Quiz
                      </button>
                      <button 
                        onClick={confirmLeaveQuiz}
                        className="btn btn-primary"
                      >
                        Leave Anyway
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Lesson content */}
              {currentModule.type === 'lesson' && (
                <div>
                  <div 
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: currentModule.content }}
                  ></div>
                  
                  <div className="mt-8 flex justify-between">
                    <button
                      onClick={navigatePrevious}
                      className={`btn btn-outline ${currentModuleIndex === 0 ? 'invisible' : ''}`}
                    >
                      Previous
                    </button>
                    
                    {currentModule.completed ? (
                      <button
                        onClick={navigateNext}
                        className={`btn btn-primary ${currentModuleIndex === course.modules.length - 1 ? 'invisible' : ''}`}
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        onClick={handleMarkComplete}
                        className="btn btn-primary"
                      >
                        Mark as Complete
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Quiz content */}
              {currentModule.type === 'quiz' && (
                <div>
                  <p className="text-gray-700 mb-6">
                    Complete this quiz to test your knowledge. You need to score 70% or higher to pass.
                  </p>
                  
                  {quizSubmitted && (
                    <div className={`mb-6 p-4 rounded-md ${
                      quizScore >= 70 ? 'bg-green-50' : 'bg-red-50'
                    }`}>
                      <p className={`font-medium ${
                        quizScore >= 70 ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {quizScore >= 70 
                          ? `You scored ${quizScore}% and have passed this quiz.` 
                          : `You scored ${quizScore}%. You need 70% to pass. Please try again.`
                        }
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-8">
                    {currentModule.questions.map((question, qIndex) => (
                      <div key={question.id} className="p-4 border border-gray-200 rounded-md">
                        <h3 className="font-medium text-gray-900 mb-3">
                          {qIndex + 1}. {question.question}
                        </h3>
                        
                        <div className="space-y-2">
                          {question.options.map((option, oIndex) => (
                            <label 
                              key={oIndex}
                              className={`flex items-center p-3 rounded-md ${
                                quizSubmitted && oIndex === question.correctAnswer
                                  ? 'bg-green-50'
                                  : quizSubmitted && quizAnswers[question.id] === oIndex && oIndex !== question.correctAnswer
                                    ? 'bg-red-50'
                                    : 'hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`question-${question.id}`}
                                checked={quizAnswers[question.id] === oIndex}
                                onChange={() => handleQuizAnswerChange(question.id, oIndex)}
                                disabled={quizSubmitted}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="ml-3 text-gray-700">{option}</span>
                              
                              {quizSubmitted && oIndex === question.correctAnswer && (
                                <span className="ml-auto text-green-600 text-sm">Correct answer</span>
                              )}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-8 flex justify-between">
                    <button
                      onClick={navigatePrevious}
                      className={`btn btn-outline ${currentModuleIndex === 0 ? 'invisible' : ''}`}
                    >
                      Previous
                    </button>
                    
                    {quizSubmitted && quizScore >= 70 ? (
                      <button
                        onClick={navigateNext}
                        className={`btn btn-primary ${currentModuleIndex === course.modules.length - 1 ? 'invisible' : ''}`}
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        onClick={handleQuizSubmit}
                        disabled={Object.keys(quizAnswers).length !== currentModule.questions.length || quizSubmitted}
                        className={`btn btn-primary ${
                          Object.keys(quizAnswers).length !== currentModule.questions.length ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {quizSubmitted ? 'Retake Quiz' : 'Submit Answers'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail; 